<template>
  <v-container class="py-12">
    <v-row>
      <v-col cols="12">
        <div class="title-container">
          <div class="title-block">
            <span id="title" class="page-title">Abastecimentos</span>
            <v-breadcrumbs class="app-breadcrumb" divider="›" :items="breadcrumbs"></v-breadcrumbs>
          </div>
          <div class="button-group">
            <v-btn id="btnNew" type="button" color="primary" elevation="0" to="/abastecimentos/novo">
              <v-icon left small>mdi-plus</v-icon>
              Novo abastecimento
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card class="filter-card">
          <v-card-title class="filter-card-title">Filtros de busca</v-card-title>
          <v-card-text>
            <v-form @submit.prevent="applyFilter">
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field dense outlined hide-details label="Frota"
                    v-model="filters.vehicle_number" clearable></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field dense outlined hide-details label="Placa"
                    v-model="filters.plate" clearable></v-text-field>
                </v-col>
                <v-col cols="12" md="4" class="d-flex align-center">
                  <v-btn id="btnFilter" type="submit" color="primary" dark elevation="2">Aplicar filtros</v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-text>
            <v-data-table
              :headers="headers"
              :items="items"
              :loading="loading"
              :server-items-length="total"
              :items-per-page="pagination.limit"
              @update:page="onPage"
              @update:items-per-page="onLimit"
              :footer-props="footerProps"
              no-data-text="Nenhum abastecimento encontrado"
              loading-text="Carregando dados..."
              class="app-table hover-table"
            >
              <template v-slot:[`item.vehicle`]="{ item }">
                <div class="font-weight-medium">{{ item.vehicle_code || item.current_vehicle_code || '-' }}</div>
                <div class="caption grey--text">{{ item.plate || item.current_plate || '-' }} {{ item.brand || '' }} {{ item.model || '' }}</div>
              </template>

              <template v-slot:[`item.cost`]="{ item }">
                <span class="font-weight-medium">{{ formatCurrency(item.cost || item.total_value) }}</span>
              </template>

              <!-- Combustível, quantidade e preço numa coluna só -->
              <template v-slot:[`item.liters`]="{ item }">
                <div class="text-no-wrap">{{ Number(item.cubic_meters) > 0 ? formatCubicMeters(item.cubic_meters) : formatLiters(item.liters) }}</div>
                <div class="caption grey--text text-no-wrap">
                  {{ item.fuel_type || 'Combustível' }}<template v-if="Number(item.unit_price) > 0"> · {{ formatCurrency(item.unit_price) }}/{{ Number(item.cubic_meters) > 0 ? 'm³' : 'L' }}</template>
                </div>
              </template>

              <template v-slot:[`item.odometer`]="{ item }">
                <div class="text-no-wrap">{{ item.odometer }}</div>
                <v-chip v-if="item.odometer_alert" x-small color="warning" text-color="black" :title="item.odometer_alert_message || ''">
                  Alerta KM
                </v-chip>
              </template>

              <template v-slot:[`item.evidence`]="{ item }">
                <span class="text-no-wrap">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon small :color="item.odometer_photo_id ? 'green' : 'grey'" v-bind="attrs" v-on="on">
                      mdi-counter
                    </v-icon>
                  </template>
                  <span>{{ item.odometer_photo_id ? 'Foto do odômetro enviada' : 'Sem foto do odômetro' }}</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon small class="ml-2" :color="item.invoice_photo_id ? 'green' : 'grey'" v-bind="attrs" v-on="on">
                      mdi-receipt-text-outline
                    </v-icon>
                  </template>
                  <span>{{ item.invoice_photo_id ? 'Nota fiscal enviada' : 'Sem nota fiscal' }}</span>
                </v-tooltip>
                </span>
              </template>

              <template v-slot:[`item.usage_date`]="{ item }">
                <div class="text-no-wrap">{{ item.usage_date | defaultDate('DD/MM/YYYY HH:mm') }}</div>
                <div class="caption grey--text text-no-wrap">enviado {{ item.created_at | defaultDate('DD/MM HH:mm') }}</div>
              </template>

              <template v-slot:[`item.description`]="{ item }">
                <span class="clamp-2" style="min-width: 140px; max-width: 170px" :title="item.description || ''">{{ item.description || '-' }}</span>
              </template>

              <template v-slot:[`item.driver_name`]="{ item }">
                <span class="d-inline-block text-truncate" style="max-width: 130px" :title="item.driver_name || ''">{{ item.driver_name || '-' }}</span>
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small color="primary" v-bind="attrs" v-on="on" @click="$router.push('/abastecimentos/' + item.id)">
                      <v-icon small>mdi-eye-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Visualizar</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from 'axios'

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
})

export default {
  name: 'SuppliesList',
  data: () => ({
    breadcrumbs: [
      { text: 'Home', disabled: false, to: '/home', exact: true },
      { text: 'Abastecimentos', disabled: true, exact: true },
    ],
    headers: [
      { text: 'Veículo', value: 'vehicle', sortable: false },
      { text: 'KM', value: 'odometer' },
      { text: 'Abastecimento', value: 'liters' },
      { text: 'Custo', value: 'cost' },
      { text: 'Fotos', value: 'evidence', sortable: false },
      { text: 'Descrição', value: 'description', sortable: false },
      { text: 'Motorista', value: 'driver_name' },
      { text: 'Data', value: 'usage_date' },
      { text: '', value: 'actions', sortable: false, width: '56px' },
    ],
    footerProps: {
      itemsPerPageText: 'Itens por página:',
      itemsPerPageOptions: [10, 20, 30, 40, 50],
      pageText: '{0}-{1} de {2}',
    },
    items: [],
    total: 0,
    loading: false,
    pagination: { page: 1, limit: 10 },
    filters: {
      vehicle_number: '',
      plate: '',
    },
  }),

  created() {
    this.load()
  },

  methods: {
    formatCurrency(value) {
      return currencyFormatter.format(Number(value || 0))
    },

    formatLiters(value) {
      const number = Number(value || 0)
      return `${number.toLocaleString('pt-BR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 3,
      })} L`
    },

    formatCubicMeters(value) {
      const number = Number(value || 0)
      return `${number.toLocaleString('pt-BR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 6,
      })} m³`
    },

    buildSearch() {
      const search = {}
      if (this.filters.vehicle_number) search.vehicle_number = this.filters.vehicle_number
      if (this.filters.plate) search.plate = this.filters.plate
      return JSON.stringify(search)
    },

    applyFilter() {
      this.pagination.page = 1
      this.load()
    },

    async load() {
      this.loading = true

      try {
        const { data } = await axios.get('/api/v2/fuel-supplies', {
          params: {
            page: this.pagination.page,
            limit: this.pagination.limit,
            search: this.buildSearch(),
          },
        })

        const content = data?.content || {}
        this.items = content.data || []
        this.total = content.total || 0
      } catch (error) {
        this.$dialog.notify.error(this.$codes ? this.$codes(error) : 'Erro ao carregar abastecimentos.', {
          position: 'top-right',
          timeout: 5000,
        })
      } finally {
        this.loading = false
      }
    },

    onPage(page) {
      this.pagination.page = page
      this.load()
    },

    onLimit(limit) {
      this.pagination.limit = limit
      this.pagination.page = 1
      this.load()
    },
  },
}
</script>
